import type { ExternalTableSchema } from '@packages/table-builder';

export const tradeOrderListTable: ExternalTableSchema = {
  metadata: {
    title: 'Trade Order',
    entity: 'TradeOrder',
  },

  dataSource: {
    endpoint: '/api/award-settlement/api/app/trade-order/placed-orders',
    method: 'GET',
    rowIdField: 'tradeOrderId',
    responseMapping: {
      items: 'items',
      totalCount: 'totalCount',
    },
    requestParams: {
      Filter: 'table.searchText',
      Sorting: 'table.sorting',
      SkipCount: 'table.pageOffset',
      MaxResultCount: 'table.pageSize',
      OrderStatusId: 'orderStatusId',
      CreatedDate: 'createdDate',
      CompanyIds: 'companyIds',
      CompanyTicker: 'companyTicker',
    },
    saveActionId: 'SAVE_TRADE_ORDER',
    saveSuccessMessage: 'Trade Order Edited.',
    onSaveSuccess: 'toastAndRefetch',
    confirmBeforeSave: {
      title: 'Are you sure you want to Save?',
      message: '',
      confirmLabel: 'Confirm',
      cancelLabel: 'Cancel',
      variant: 'warning',
      titleAlign: 'center',
    },
    filters: [
      {
        name: 'orderStatusId',
        placeholder: 'Order Status',
        type: 'asyncSelect',
        dataSource: {
          url: '/api/award-settlement/api/app/trade-order/trade-order-status',
          labelField: 'displayName',
          valueField: 'id',
        },
      },
      {
        name: 'createdDate',
        placeholder: 'Created Date',
        type: 'date',
      },
    ],
  },

  computeRules: [
    {
      sourceFields: ['soldShares', 'salePrice'],
      operator: 'multiply',
      targetField: 'cashProceeds',
      precision: 4,
    },
  ],

  columns: [
    { field: 'createdDate', header: 'Created Date', type: 'dateTime', sortable: true, width: 180, align: 'center' },
    { field: 'orderType', header: 'Order Type', sortable: true, width: 150 },
    { field: 'company', header: 'Company', sortable: true, width: 200 },
    { field: 'ticker', header: 'Ticker', sortable: true, width: 100 },
    { field: 'shares', header: 'Shares', type: 'number', sortable: true, width: 100, editable: { type: 'number' } },
    { field: 'brokerSellType', header: 'Broker Sell Type', sortable: true, width: 150 },
    {
      field: 'fundingRequired',
      header: 'Funding Required',
      type: 'currency',
      sortable: true,
      width: 150,
      align: 'center',
      prefix: '$',
      format: { decimals: 2 },
      editable: { type: 'number' },
    },
    { field: 'maxShares', header: 'Max Shares', type: 'number', sortable: true, width: 100, align: 'center', editable: { type: 'number' } },
    { field: 'brokerName', header: 'Broker Name', sortable: true, width: 200 },
    { field: 'brokerAccount', header: 'Broker Account', sortable: true, width: 200 },
    { field: 'bulkExecutionName', header: 'Bulk Execution Name', sortable: true, width: 200 },
    {
      field: 'orderStatus',
      header: 'Order Status',
      type: 'badge',
      sortable: true,
      width: 150,
      badge: {
        statusMap: {
          Executed: 'success',
          Received: 'success',
          Inactive: 'warning',
          Suspended: 'error',
          Cancelled: 'error',
          Reversed: 'primary',
        },
        defaultVariant: 'info',
      },
      editable: { type: 'asyncSelect', defaultValueField: 'orderStatus' },
      dataSource: {
        url: '/api/award-settlement/api/app/trade-order/trade-order-status',
        mapper: { label: 'displayName', value: 'displayName' },
      },
    },
    { field: 'cancelStatus', header: 'Cancel Status', sortable: true, width: 150 },
    { field: 'cancelRequestedDate', header: 'Cancel Requested Date', type: 'date', sortable: true, width: 180, align: 'center' },
    { field: 'orderNumber', header: 'Order Number', type: 'text', sortable: true, width: 120, editable: { type: 'text' } },
    { field: 'orderExecutedDate', header: 'Order Executed Date', type: 'date', sortable: true, width: 200, align: 'center', editable: { type: 'date' } },
    {
      field: 'soldShares', header: 'Sold Shares', type: 'number', sortable: true, width: 90,
      format: { decimals: 0 },
      editable: { type: 'number', precision: 0 },
    },
    {
      field: 'fee',
      header: 'Fee',
      type: 'currency',
      sortable: true,
      width: 90,
      align: 'center',
      prefix: '$',
      format: { decimals: 2 },
      editable: { type: 'number' },
    },
    {
      field: 'commision',
      header: 'Commission',
      type: 'currency',
      sortable: true,
      width: 90,
      align: 'center',
      prefix: '$',
      format: { decimals: 2 },
    },
    {
      field: 'bidPrice',
      header: 'Bid Price',
      type: 'currency',
      sortable: false,
      width: 90,
      align: 'center',
      prefix: '$',
      format: { decimals: 2 },
      editable: { type: 'number' },
    },
    {
      field: 'salePrice',
      header: 'Sale Price',
      type: 'currency',
      sortable: true,
      width: 90,
      align: 'center',
      prefix: '$',
      format: { decimals: 4 },
      editable: { type: 'number' },
    },
    {
      field: 'cashProceeds',
      header: 'Cash Proceeds',
      type: 'currency',
      sortable: true,
      width: 90,
      align: 'center',
      prefix: '$',
      format: { decimals: 4 },
      editable: { type: 'number' },
    },
    { field: 'tradeOrderCode', header: 'Trade Order Code', sortable: true, width: 150 },
  ],

  actions: {
    row: [{ id: 'EDIT_TRADE_ORDER', label: 'Edit', actionType: 'startEditing' }],
  },

  features: {
    search: { enabled: false },
    pagination: { mode: 'infiniteScroll', defaultSize: 50 },
    editing: { bulkSave: true },
    columnPreferences: { persistenceKey: 'TradeOrderList', resizable: true, reorderable: true },
  },
};
